/**
 * Project — single project view with progressive disclosure layout.
 *
 * The DAG fills the screen on first load. The slide panel is opened
 * exclusively via the toolbar Explore toggle — never auto-opened by
 * node clicks (which caused disorienting simultaneous layout changes).
 *
 * Toolbar structure (left → right):
 *   Left:  Logo | sep | ← nav / breadcrumb | sep | Branch | Commit | Re-import
 *   Right: [Explore toggle] | sep | Reproducibility | Config | Members
 *
 * Explore toggle: closed → opens to last-used tab (default "data").
 *                 active  → closes the panel. Label shows active tab.
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Box, Button, Dialog, Flex, Heading, Text } from "@radix-ui/themes";

import { ProjectLayout } from "../components/layout/ProjectLayout";
import { DockingLayout } from "../components/layout/DockingLayout";
import { ImportProgressBanner } from "../components/shared/ImportProgressBanner";
import { ProvenanceProvider, useProvenanceContext, type PanelTab } from "../context/ProvenanceContext";
import { useLayoutModeStore } from "../stores/layoutModeStore";
import { RefAreaPreviewProvider } from "../context/RefAreaPreviewContext";
import { PlotDataProvider } from "../context/PlotDataContext";
import { ViewStateProvider } from "../context/ViewStateContext";
import { BranchGuardProvider } from "../context/BranchGuardContext";
import { useAnonymous } from "../context/AnonymousContext";
import { fetchProjects, fetchProject, reimportProject, resolveSlug } from "../api/projects";
import { ThreadLogo } from "../components/ThreadLogo";
import { ShareButton } from "../components/ShareButton";
import { ThemeToggle } from "../components/toolbar/ThemeToggle";
import { SemanticSearch } from "../components/search/SemanticSearch";
import { BranchSelector, CommitDialog } from "../components/versioning";
import { MembersPanel } from "../components/members/MembersPanel";
import { useMembers } from "../hooks/useMembers";
import { useCurrentRole } from "../hooks/useCurrentRole";
import { OnboardingProvider, useOnboarding, EmbedBridge } from "../shell/onboarding";
import { threadOnboardingConfig } from "../shell/onboarding/threadTours";

// ---------------------------------------------------------------------------
// Explore toggle — single button that opens/closes the slide panel.
// Must be inside ProvenanceProvider to access context.
// ---------------------------------------------------------------------------

// Remembers the last-used tab so toggling re-opens to the same tab.
const DEFAULT_PANEL: PanelTab = "data";

function ExploreToggle() {
  const { openPanel, setOpenPanel } = useProvenanceContext();
  const lastTabRef = useRef<PanelTab>(DEFAULT_PANEL);
  if (openPanel) lastTabRef.current = openPanel;

  const isOpen = openPanel !== null;

  return (
    <Button
      data-tour="explore-toggle"
      size="1"
      variant={isOpen ? "solid" : "soft"}
      onClick={() => setOpenPanel(isOpen ? null : (lastTabRef.current ?? DEFAULT_PANEL))}
    >
      <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
        <path
          d="M1.5 3a.5.5 0 0 0 0 1h12a.5.5 0 0 0 0-1h-12Zm0 4a.5.5 0 0 0 0 1h12a.5.5 0 0 0 0-1h-12Zm0 4a.5.5 0 0 0 0 1h12a.5.5 0 0 0 0-1h-12Z"
          fill="currentColor"
        />
      </svg>
      Explore
    </Button>
  );
}

// ---------------------------------------------------------------------------
// View menu — toggle between Standard and Advanced (docking) layout.
// ---------------------------------------------------------------------------

function ViewMenu() {
  const { mode, setMode } = useLayoutModeStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <Button variant="ghost" size="1" color="gray" onClick={() => setOpen((v) => !v)} title="Layout mode">
        <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
          <path d="M1 2.5a.5.5 0 0 1 .5-.5h12a.5.5 0 0 1 .5.5v10a.5.5 0 0 1-.5.5h-12a.5.5 0 0 1-.5-.5v-10ZM2 3v9h5V3H2Zm6 0v4h5V3H8Zm5 5H8v4h5V8Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
        </svg>
        View
      </Button>

      {open && (
        <div
          style={{
            position: "absolute", top: "100%", right: 0, marginTop: 4,
            background: "var(--color-surface, #1a1a2e)", border: "1px solid var(--gray-a5)",
            borderRadius: 6, padding: 4, minWidth: 180, zIndex: 20,
            boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          }}
        >
          <div style={{ padding: "4px 10px 6px", fontSize: 10, fontWeight: 600, color: "var(--gray-9)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Layout
          </div>
          {(["standard", "advanced"] as const).map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setOpen(false); }}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                width: "100%", padding: "6px 10px", fontSize: 12,
                color: "var(--gray-12)", background: mode === m ? "var(--accent-a3)" : "transparent",
                border: "none", borderRadius: 4, cursor: "pointer",
                fontFamily: "inherit", textAlign: "left", fontWeight: mode === m ? 600 : 400,
              }}
              onMouseEnter={(e) => { if (mode !== m) (e.currentTarget as HTMLElement).style.background = "var(--gray-a3)"; }}
              onMouseLeave={(e) => { if (mode !== m) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
            >
              <span style={{ width: 14, textAlign: "center" }}>{mode === m ? "\u2713" : ""}</span>
              {m === "standard" ? "Standard" : "Advanced (Docking)"}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Help / Tour button — dropdown with available tours + reset option.
// Must be inside OnboardingProvider.
// ---------------------------------------------------------------------------

function HelpButton() {
  const { startTour, isActive, resetAllTours } = useOnboarding();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const tours = [
    { id: "welcome", label: "Welcome Tour" },
    { id: "data-explorer", label: "Data Explorer Tour" },
    { id: "dag-navigation", label: "DAG Navigation Tour" },
    { id: "comments", label: "Comments & Collaboration Tour" },
    { id: "sref-mystery", label: "S_ref Mystery" },
  ];

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <Button
        variant="ghost"
        size="1"
        color="gray"
        title="Help & Tours"
        onClick={() => setOpen((v) => !v)}
        disabled={isActive}
      >
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0.877075 7.49972C0.877075 3.84204 3.84222 0.876892 7.49991 0.876892C11.1576 0.876892 14.1227 3.84204 14.1227 7.49972C14.1227 11.1574 11.1576 14.1226 7.49991 14.1226C3.84222 14.1226 0.877075 11.1574 0.877075 7.49972ZM7.49991 1.82689C4.36689 1.82689 1.82708 4.36671 1.82708 7.49972C1.82708 10.6327 4.36689 13.1726 7.49991 13.1726C10.6329 13.1726 13.1727 10.6327 13.1727 7.49972C13.1727 4.36671 10.6329 1.82689 7.49991 1.82689ZM8.24993 10.5C8.24993 10.9142 7.91414 11.25 7.49993 11.25C7.08571 11.25 6.74993 10.9142 6.74993 10.5C6.74993 10.0858 7.08571 9.75 7.49993 9.75C7.91414 9.75 8.24993 10.0858 8.24993 10.5ZM6.05003 6.25C6.05003 5.44919 6.69922 4.8 7.50003 4.8C8.30084 4.8 8.95003 5.44919 8.95003 6.25C8.95003 6.74924 8.68683 7.18688 8.29163 7.44276C8.11093 7.56066 7.95003 7.70162 7.95003 7.9V8.5C7.95003 8.74853 7.74856 8.95 7.50003 8.95C7.2515 8.95 7.05003 8.74853 7.05003 8.5V7.9C7.05003 7.22835 7.58693 6.90459 7.88773 6.7082C8.07773 6.58399 8.05003 6.41394 8.05003 6.25C8.05003 5.94624 7.80379 5.7 7.50003 5.7C7.19627 5.7 6.95003 5.94624 6.95003 6.25C6.95003 6.49853 6.74856 6.7 6.50003 6.7C6.2515 6.7 6.05003 6.49853 6.05003 6.25Z"
            fill="currentColor"
            fillRule="evenodd"
            clipRule="evenodd"
          />
        </svg>
      </Button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 4,
            background: "var(--color-surface, #1a1a2e)",
            border: "1px solid var(--gray-a5)",
            borderRadius: 6,
            padding: 4,
            minWidth: 180,
            zIndex: 20,
            boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          }}
        >
          <div style={{ padding: "4px 10px 6px", fontSize: 10, fontWeight: 600, color: "var(--gray-9)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Tours
          </div>
          {tours.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                startTour(t.id, true);
                setOpen(false);
              }}
              style={{
                display: "block",
                width: "100%",
                padding: "6px 10px",
                fontSize: 12,
                color: "var(--gray-12)",
                background: "transparent",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "inherit",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--gray-a3)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
            >
              {t.label}
            </button>
          ))}
          <div style={{ height: 1, background: "var(--gray-a5)", margin: "4px 0" }} />
          <button
            onClick={() => {
              resetAllTours();
              setOpen(false);
            }}
            style={{
              display: "block",
              width: "100%",
              padding: "6px 10px",
              fontSize: 11,
              color: "var(--gray-9)",
              background: "transparent",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              textAlign: "left",
              fontFamily: "inherit",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--gray-a3)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
          >
            Reset Tutorial Progress
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toast notification — auto-dismisses after 4s, no library needed
// ---------------------------------------------------------------------------

interface ToastState {
  msg: string;
  variant: "success" | "error";
}

function Toast({ toast }: { toast: ToastState }) {
  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        padding: "10px 16px",
        borderRadius: 8,
        fontSize: 12,
        fontFamily: "inherit",
        background: toast.variant === "error" ? "var(--red-9)" : "var(--grass-9)",
        color: "white",
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        zIndex: 10000,
        maxWidth: 400,
        lineHeight: 1.5,
      }}
    >
      {toast.msg}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OnboardingBridge — lives inside ProvenanceProvider so it can wire
// setOpenPanel into the OnboardingProvider as a plain callback.
// ---------------------------------------------------------------------------

function OnboardingBridge({ children }: { children: React.ReactNode }) {
  const { setOpenPanel } = useProvenanceContext();
  const handleOpenPanel = useCallback(
    (panelId: string | null) => setOpenPanel(panelId as PanelTab | null),
    [setOpenPanel],
  );
  return (
    <OnboardingProvider config={threadOnboardingConfig} onOpenPanel={handleOpenPanel}>
      {children}
    </OnboardingProvider>
  );
}

// ---------------------------------------------------------------------------
// TourAutoStart — reads ?tour= from URL and kicks off the tour once
// ---------------------------------------------------------------------------

function TourAutoStart() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { startTour, isActive } = useOnboarding();
  const firedRef = useRef(false);

  const tourId = searchParams.get("tour");

  useEffect(() => {
    if (!tourId || firedRef.current || isActive) return;

    const timer = setTimeout(() => {
      if (firedRef.current) return;
      firedRef.current = true;
      startTour(tourId, true);
      const next = new URLSearchParams(searchParams);
      next.delete("tour");
      setSearchParams(next, { replace: true });
    }, 1500);
    return () => clearTimeout(timer);
  }, [tourId, startTour, isActive, searchParams, setSearchParams]);

  return null;
}

// ---------------------------------------------------------------------------
// Layout switch — renders Standard or Advanced layout based on store.
// ---------------------------------------------------------------------------

function LayoutSwitch({ projectId }: { projectId: string }) {
  const { mode } = useLayoutModeStore();
  if (mode === "advanced") {
    return <DockingLayout projectId={projectId} />;
  }
  return <ProjectLayout projectId={projectId} />;
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Project() {
  const { projectId: rawId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const importJobId = searchParams.get("importJob") || undefined;
  const [reimporting, setReimporting] = useState(false);
  const [commitOpen, setCommitOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isUUID = rawId ? /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(rawId) : false;

  const { data: resolvedId } = useQuery({
    queryKey: ["resolve-slug", rawId],
    queryFn: () => resolveSlug(rawId!),
    enabled: !!rawId && !isUUID,
    staleTime: Infinity,
    retry: false,
  });

  const projectId = isUUID ? rawId : resolvedId;

  const { isAnonymous, isEmbedded } = useAnonymous();
  const { data: members } = useMembers(isAnonymous ? undefined : projectId);
  const { canEdit } = useCurrentRole(isAnonymous ? undefined : projectId);

  const { data: projects } = useQuery({
    queryKey: ["projects"],
    queryFn: fetchProjects,
    staleTime: 60_000,
    enabled: !isAnonymous,
  });

  const { data: singleProject } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => fetchProject(projectId!),
    staleTime: 60_000,
    enabled: isAnonymous && !!projectId,
  });

  const projectName = isAnonymous
    ? singleProject?.name
    : projects?.find((p) => p.id === projectId)?.name;

  function showToast(msg: string, variant: "success" | "error") {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, variant });
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }

  async function handleReimport() {
    if (!projectId || reimporting) return;
    setReimporting(true);
    try {
      const result = await reimportProject(projectId);
      showToast(
        `Imported ${result.cases_imported} run(s), ${result.nodes_created} node(s) total` +
          (result.cases_skipped ? `, ${result.cases_skipped} skipped` : ""),
        "success",
      );
      queryClient.invalidateQueries({ queryKey: ["run-table", projectId] });
      queryClient.invalidateQueries({ queryKey: ["provenance", projectId] });
      queryClient.invalidateQueries({ queryKey: ["nodes", projectId] });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(`Re-import failed: ${msg}`, "error");
    } finally {
      setReimporting(false);
    }
  }

  if (!projectId) {
    return <Heading>Project not found</Heading>;
  }

  return (
    <ProvenanceProvider projectId={projectId}>
    <RefAreaPreviewProvider>
    <BranchGuardProvider>
    <ViewStateProvider>
    <PlotDataProvider>
    <OnboardingBridge>
      <TourAutoStart />
      {isEmbedded && <EmbedBridge config={threadOnboardingConfig} />}
      <Box style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        {/* Toolbar — compact in embed mode, full otherwise */}
        {isEmbedded ? (
          <Flex
            align="center"
            justify="between"
            px="3"
            py="1"
            style={{
              borderBottom: "1px solid var(--gray-a4)",
              background: "var(--color-background)",
              flexShrink: 0,
            }}
          >
            <Flex align="center" gap="3">
              <ThreadLogo height={18} />
              {projectName && <Text size="2" weight="medium">{projectName}</Text>}
              <span data-tour="branch-selector"><BranchSelector /></span>
            </Flex>
            <Flex align="center" gap="2">
              <ExploreToggle />
              <Button
                variant="ghost"
                size="1"
                color="blue"
                onClick={() => {
                  const url = window.location.href.replace("/demo/", "/project/").replace(/[?&]embed=1/, "");
                  window.open(url, "_blank");
                }}
              >
                Open in Thread
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Flex
            align="center"
            justify="between"
            px="3"
            py="2"
            style={{
              borderBottom: "1px solid var(--gray-a4)",
              background: "var(--color-background)",
              flexShrink: 0,
            }}
          >
            {/* Left: navigation + versioning + data-sync */}
            <Flex align="center" gap="3">
              <ThreadLogo height={22} onClick={() => navigate("/")} />

              <Box style={{ width: 1, height: 20, background: "var(--gray-a5)" }} />

              {!isAnonymous && (
                <Button variant="ghost" size="1" color="gray" onClick={() => navigate("/")}>
                  <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                    <path
                      d="M6.85 3.15a.5.5 0 0 1 0 .7L3.56 7H12.5a.5.5 0 0 1 0 1H3.56l3.29 3.15a.5.5 0 1 1-.7.7l-4-3.85a.5.5 0 0 1 0-.7l4-3.85a.5.5 0 0 1 .7 0z"
                      fill="currentColor"
                    />
                  </svg>
                  Projects
                </Button>
              )}

              {projectName && (
                <>
                  {!isAnonymous && <Text size="2" color="gray">/</Text>}
                  <Text size="2" weight="medium">{projectName}</Text>
                </>
              )}

              <Box style={{ width: 1, height: 20, background: "var(--gray-a5)" }} />

              <span data-tour="branch-selector"><BranchSelector /></span>

              {!isAnonymous && (
                <>
                  <Button
                    variant="soft"
                    size="1"
                    onClick={() => setCommitOpen(true)}
                    disabled={!canEdit}
                    title={!canEdit ? "You need editor permissions to commit" : undefined}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.75.75 0 0 1 1.06-1.06L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0Z" />
                    </svg>
                    Commit
                  </Button>

                  <Button
                    size="1"
                    variant="soft"
                    disabled={reimporting || !canEdit}
                    onClick={handleReimport}
                  >
                    <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                      <path
                        d="M1.85 7.5c0-2.84 2.16-5.17 4.93-5.46V.54a6.99 6.99 0 0 0-1.5 12.82l.72-1.22A5.5 5.5 0 0 1 1.85 7.5Zm11.3 0A5.5 5.5 0 0 1 9 12.86l-.72 1.22A7 7 0 0 0 9.72.54v1.5A5.5 5.5 0 0 1 13.15 7.5Z"
                        fill="currentColor"
                      />
                    </svg>
                    {reimporting ? "Re-importing..." : "Re-import"}
                  </Button>
                </>
              )}
            </Flex>

            {/* Right: search + explore toggle + share + help + members + theme */}
            <Flex align="center" gap="2">
              <SemanticSearch />
              <ExploreToggle />
              <ViewMenu />
              {!isAnonymous && <ShareButton />}
              {!isAnonymous && <HelpButton />}

              {isAnonymous && (
                <Button variant="soft" size="1" color="blue" onClick={() => navigate("/")}>
                  Sign in
                </Button>
              )}

              {!isAnonymous && (
                <>
                  <Box style={{ width: 1, height: 20, background: "var(--gray-a5)" }} />

                  {/* Project members */}
                  <Dialog.Root open={membersOpen} onOpenChange={setMembersOpen}>
                    <Dialog.Trigger>
                      <Button variant="ghost" size="1" color="gray">
                        <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
                          <path
                            d="M7.5 0.875a2.625 2.625 0 1 0 0 5.25 2.625 2.625 0 0 0 0-5.25ZM0.5 12.125C0.5 9.57 2.57 7.5 5.125 7.5h4.75C12.43 7.5 14.5 9.57 14.5 12.125v1.375a.5.5 0 0 1-.5.5H1a.5.5 0 0 1-.5-.5v-1.375Z"
                            fill="currentColor"
                          />
                        </svg>
                        {members && members.length > 0 && (
                          <Badge size="1" color="gray" variant="surface">
                            {members.filter((m) => !m.pending).length}
                          </Badge>
                        )}
                      </Button>
                    </Dialog.Trigger>
                    <Dialog.Content style={{ maxWidth: 680 }}>
                      <Dialog.Title>Project Members</Dialog.Title>
                      <MembersPanel projectId={projectId} />
                    </Dialog.Content>
                  </Dialog.Root>
                </>
              )}

              <ThemeToggle />
            </Flex>
          </Flex>
        )}

        {!isAnonymous && <CommitDialog open={commitOpen} onOpenChange={setCommitOpen} />}
        <ImportProgressBanner projectId={projectId} jobId={importJobId} />
        <LayoutSwitch projectId={projectId} />
      </Box>

      {/* Auto-dismissing toast notification */}
      {toast && <Toast toast={toast} />}
    </OnboardingBridge>
    </PlotDataProvider>
    </ViewStateProvider>
    </BranchGuardProvider>
    </RefAreaPreviewProvider>
    </ProvenanceProvider>
  );
}
